'use client';

import { c as _c } from "react/compiler-runtime";
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import type { MPAInterceptedResponse, MPAPolicy } from '@repo/api-gw-sdk';
import { MPAOperationScope, MPAOperationType } from '@repo/api-gw-sdk';
import { Fragment, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@/components/accordion';
import { operationVerbs } from '@/components/mpa/constants';
import { useSamlGroupSupport } from '@/components/mpa/useSamlGroupSupport';
import { Icon } from '@/components/shared/icon';
import { JsonViewer } from '@/components/shared/jsonViewer';
import { useDAL } from '@/contexts/useDAL';
import { MpaOperations } from '@/data/inventory/data';
import { parseJson } from '@/utils/string';
import { BackupPolicyEntity, ResourceEntity, RestoreAccountEntity, getBackupPolicyId, getResourceId } from './mpaRequestEntity';
type Pending = 'approve' | 'cancel' | null;
const formatHours = (hours: number) => `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
const formatApprovers = (n: number) => `${n} ${n === 1 ? 'approver' : 'approvers'}`;
export function MpaInterceptDialogContent({
  payload,
  onApprove,
  onCancel,
  showDetails = false,
  onShowDetails
}: {
  payload: MPAInterceptedResponse;
  onApprove: (comment: string) => Promise<void>;
  onCancel: () => Promise<void>;
  showDetails?: boolean;
  onShowDetails?: () => void;
}) {
  const dal = useDAL();
  const {
    data: operationsBody
  } = dal.mpa.operations.useList();
  const {
    samlGroupById
  } = useSamlGroupSupport();
  const [pending, setPending] = useState<Pending>(null);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const policies = payload.mpaPolicies?.policies ?? [];
  const operation = policies[0]?.operation;
  const operationName = operation ? MpaOperations[operation]?.name ?? '' : '';
  const scope = operation ? operationsBody?.operations.find(o => o.operation === operation)?.scope : undefined;
  const isBackupPolicyScope = scope === MPAOperationScope.MPA_OPERATION_SCOPE_BACKUP_POLICY;
  const isCloudResourceScope = scope === MPAOperationScope.MPA_OPERATION_SCOPE_CLOUD_RESOURCE;
  const isAddRestoreAccount = operation === MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_ADD_RESTORE_ACCOUNT;
  const run = (kind: Exclude<Pending, null>, action: () => Promise<void>) => async () => {
    setError(null);
    setPending(kind);
    try {
      await action();
    } catch (e) {
      setError((e as Error).message);
      setPending(null);
    }
  };
  const handleApprove = run('approve', () => onApprove(comment));
  const handleCancel = run('cancel', onCancel);
  const disabled = pending !== null;
  return <Stack data-testid='mpa-intercept-dialog' className='h-full' sx={{
    overflow: 'hidden'
  }}>
      <Stack sx={{
      flex: 1,
      minHeight: 0,
      position: 'relative'
    }}>
        <Stack sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        visibility: showDetails ? 'hidden' : 'visible'
      }}>
          <Stack alignItems='center' gap='24px' sx={{
          flexShrink: 0
        }}>
            <Stack alignItems='center' gap='24px' sx={{
            width: '480px',
            textAlign: 'center',
            pt: '8px'
          }}>
              <Icon iconClass='material-symbols-approval-outline' />
              <Typography variant='h2'>Approval required</Typography>
              <Typography variant='body1' color='text.secondary'>
                This action requires approval. Once your request is approved,
                you&apos;ll have a limited time to apply it yourself.
              </Typography>
            </Stack>

            <Stack sx={{
            width: '582px'
          }}>
              <TextField fullWidth multiline minRows={1} placeholder='Tell approvers why this action is needed (optional)' value={comment} onChange={e_0 => setComment(e_0.target.value)} disabled={disabled} sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '0 100px 100px 64px',
                padding: '12px 16px',
                fontSize: '13px',
                lineHeight: '20px'
              },
              '& .MuiOutlinedInput-input': {
                padding: 0
              }
            }} />
            </Stack>
          </Stack>

          <Stack alignItems='center' sx={{
          py: '24px'
        }}>
            <Stack sx={{
            width: '582px',
            pb: '24px'
          }}>
              <Stack direction='row' alignItems='center' justifyContent='space-between' gap='12px' sx={{
              backgroundColor: 'var(--mui-palette-background-secondary)',
              borderRadius: '8px',
              px: '40px',
              py: '24px'
            }}>
                <Stack direction='row' alignItems='center' gap='12px'>
                  <Typography variant='subtitle1'>
                    You&rsquo;re requesting to{' '}
                    {operation ? operationVerbs[operation] : 'perform'}
                  </Typography>
                  {isBackupPolicyScope ? <BackupPolicyEntity policyId={getBackupPolicyId(payload.mpaRequest)} /> : isCloudResourceScope ? <ResourceEntity resourceId={getResourceId(payload.mpaRequest)} /> : isAddRestoreAccount ? <RestoreAccountEntity request={payload.mpaRequest} /> : null}
                </Stack>
                {onShowDetails && payload.mpaRequest?.requestBody && <IconButton size='small' onClick={onShowDetails} data-testid='mpa-intercept-show-details' sx={{
                borderRadius: '4px'
              }}>
                    <Icon iconClass='material-symbols-data-object' />
                  </IconButton>}
              </Stack>

              {policies.map(policy => <Fragment key={policy.id}>
                  <Connector />
                  <PolicyApprovalCard policy={policy} operationName={operationName} samlGroupName={policy.approverIdpGroupId ? samlGroupById[policy.approverIdpGroupId]?.providerGroupId : undefined} />
                </Fragment>)}

              {error && <Typography color='error' variant='body2' sx={{
              pt: '16px'
            }}>
                  {error}
                </Typography>}
            </Stack>
          </Stack>
        </Stack>

        {showDetails && payload.mpaRequest?.requestBody && <Stack sx={{
        position: 'absolute',
        inset: '0 0 5px 0',
        backgroundColor: 'var(--mui-palette-background-paper)',
        overflow: 'auto',
        px: '40px',
        py: '24px'
      }}>
            <JsonViewer data={parseJson<object>(payload.mpaRequest.requestBody) ?? {}} />
          </Stack>}
      </Stack>

      <Stack direction='row' gap='12px' justifyContent='flex-end' alignItems='center' sx={{
      flexShrink: 0,
      backgroundColor: 'var(--mui-palette-background-paper)',
      boxShadow: 'var(--mui-customShadows-panelMd)',
      px: '40px',
      py: '16px'
    }}>
        <Button variant='text' onClick={() => void handleCancel()} disabled={disabled} data-testid='mpa-intercept-cancel'>
          Cancel Action
        </Button>
        <Button variant='contained' onClick={() => void handleApprove()} disabled={disabled} data-testid='mpa-intercept-approve'>
          Request Approval
        </Button>
      </Stack>
    </Stack>;
}
const Connector = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Box sx={{
      width: "1px",
      height: "24px",
      ml: "40px",
      backgroundColor: "var(--mui-palette-divider)"
    }} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const PolicyApprovalCard = t0 => {
  const $ = _c(39);
  const {
    policy,
    operationName,
    samlGroupName
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      backgroundColor: "var(--mui-palette-background-secondary)",
      borderRadius: "8px",
      pt: "40px",
      pb: "12px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      px: "40px"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== operationName) {
    t3 = <Typography variant="body1">{"For "}<Typography component="span" variant="h4">{operationName},{" "}</Typography>{"those approval steps are required:"}</Typography>;
    $[2] = operationName;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      pb: "24px"
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== policy.requiredApprovals) {
    t5 = formatApprovers(policy.requiredApprovals);
    $[5] = policy.requiredApprovals;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== samlGroupName) {
    t6 = samlGroupName ? <><Typography variant="subtitle1">in</Typography><Icon iconClass="material-symbols-group-outline" /><Typography variant="h4">{samlGroupName}</Typography></> : undefined;
    $[7] = samlGroupName;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <ApprovalStepRow label="From" value={t5} endAdornment={t6} />;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== policy.approvalWindowHours) {
    t8 = formatHours(policy.approvalWindowHours);
    $[12] = policy.approvalWindowHours;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <ApprovalStepRow label="Within" value={t8} />;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t7 || $[17] !== t9) {
    t10 = <ApprovalStep icon="material-symbols-approval-outline" title="Get approval">{t7}{t9}</ApprovalStep>;
    $[16] = t7;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== policy.executionWindowHours) {
    t11 = formatHours(policy.executionWindowHours);
    $[19] = policy.executionWindowHours;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11) {
    t12 = <ApprovalStep icon="material-symbols-ads-click" title="Apply the action" isLast={true}><ApprovalStepRow label="Within" value={t11} suffix="after approval" /></ApprovalStep>;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t10 || $[24] !== t12) {
    t13 = <Stack gap="24px" sx={t4}>{t10}{t12}</Stack>;
    $[23] = t10;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13 || $[27] !== t3) {
    t14 = <Stack gap="24px" sx={t2}>{t3}{t13}</Stack>;
    $[26] = t13;
    $[27] = t3;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Box sx={{
      height: "1px",
      backgroundColor: "var(--mui-palette-divider)",
      width: "100%"
    }} />;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      px: "40px"
    };
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <AccordionSummary><Typography variant="h4">Description</Typography></AccordionSummary>;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      p: 0,
      pl: "36px",
      pb: "8px"
    };
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      whiteSpace: "pre-wrap"
    };
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  const t20 = policy.description || "--";
  let t21;
  if ($[34] !== t20) {
    t21 = <Accordion sx={t16}>{t17}<AccordionDetails sx={t18}><Typography variant="body1" color="text.secondary" sx={t19}>{t20}</Typography></AccordionDetails></Accordion>;
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== t14 || $[37] !== t21) {
    t22 = <Stack gap="24px" sx={t1}>{t14}{t15}{t21}</Stack>;
    $[36] = t14;
    $[37] = t21;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  return t22;
};
const ApprovalStep = t0 => {
  const $ = _c(19);
  const {
    icon,
    title,
    isLast,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      alignSelf: "stretch"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== icon) {
    t2 = <Icon iconClass={icon} color="var(--mui-palette-text-disabled)" />;
    $[1] = icon;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== isLast) {
    t3 = !isLast && <Box sx={{
      flex: 1,
      minHeight: "24px",
      width: 0,
      borderLeft: "1px dashed var(--mui-palette-divider)"
    }} />;
    $[3] = isLast;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <Stack alignItems="center" gap="16px" sx={t1}>{t2}{t3}</Stack>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      flex: 1,
      minWidth: 0
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== title) {
    t6 = <Typography variant="h3" color="text.disabled">{title}</Typography>;
    $[9] = title;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== children) {
    t7 = <Stack gap="12px">{children}</Stack>;
    $[11] = children;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = <Stack sx={t5} gap="16px" paddingTop="4px">{t6}{t7}</Stack>;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t4 || $[17] !== t8) {
    t9 = <Stack direction="row" gap="24px" alignItems="stretch">{t4}{t8}</Stack>;
    $[16] = t4;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
};
const ApprovalStepRow = t0 => {
  const $ = _c(12);
  const {
    label,
    value,
    suffix,
    endAdornment
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      width: "40px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== label) {
    t2 = <Typography variant="subtitle1" sx={t1}>{label}</Typography>;
    $[1] = label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== value) {
    t3 = <Typography variant="h4">{value}</Typography>;
    $[3] = value;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== suffix) {
    t4 = suffix && <Typography variant="subtitle1">{suffix}</Typography>;
    $[5] = suffix;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== endAdornment || $[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = <Stack direction="row" alignItems="center" gap="12px">{t2}{t3}{endAdornment}{t4}</Stack>;
    $[7] = endAdornment;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};